import { useEffect, useRef, useState } from 'react';
import { useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { IPodcast } from '../../Interfaces/IPodcasts';
import { listarPodCasts } from '../../Services/Podcasts';

import '../../Shared/css/Podcasts/podcasts.css';


function Podcasts() {
  const user = JSON.parse(localStorage.getItem('user'));
  const [podcasts, setPodcasts] = useState<IPodcast[]>();
  const [playingId, setPlayingId] = useState<number | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 650px)');

  useEffect(() => {
    if (user) {
      listarPodCasts({
        usuarioTestAsesoriaID: user.usuarioTestAsesoriaID,
      }).then((response) => {
        setPodcasts(response.data.data);
      });
    }
  }, []);

  const formatTime = (seconds: number) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const handlePlay = (podcast: IPodcast) => {
    if (playingId === podcast.podCastClubID) {
      // Toggle pause/play
      if (audioRef.current) {
        if (audioRef.current.paused) {
          audioRef.current.play();
        } else {
          audioRef.current.pause();
        }
      }
      return;
    }

    // Play new track
    if (audioRef.current) {
      audioRef.current.pause();
    }

    const audio = new Audio(podcast.linkMp3);
    audioRef.current = audio;
    setPlayingId(podcast.podCastClubID);
    setCurrentTime(0);
    setDuration(0);

    audio.addEventListener('loadedmetadata', () => {
      setDuration(audio.duration);
    });

    audio.addEventListener('timeupdate', () => {
      setCurrentTime(audio.currentTime);
    });

    audio.addEventListener('ended', () => {
      setPlayingId(null);
      setCurrentTime(0);
    });

    audio.play();
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>, podcastId: number) => {
    if (playingId !== podcastId || !audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    audioRef.current.currentTime = percent * duration;
  };

  const isPlaying = (id: number) => {
    return playingId === id && audioRef.current && !audioRef.current.paused;
  };

  const getProgress = (id: number) => {
    if (playingId !== id || !duration) return 0;
    return (currentTime / duration) * 100;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  return (
    <div className="pod-page" style={!isMobile ? { marginTop: '6vh'} : {}}>
      {/* Header */}
      <div className="pod-nav">
        <button className="pod-nav-btn" onClick={() => navigate('/producto/2')}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
      </div>

      {/* Show Cover */}
      <div className="pod-cover">
        <div className="pod-cover-art">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
            <line x1="12" y1="19" x2="12" y2="23" />
            <line x1="8" y1="23" x2="16" y2="23" />
          </svg>
        </div>
        <div className="pod-cover-info">
          <h1 className="pod-cover-title">Audios Semanales</h1>
          <p className="pod-cover-author">Anthoni Montalvan</p>
          <p className="pod-cover-desc">Los mejores hábitos para construir el cuerpo y la vida que quieres.</p>
          <div className="pod-cover-meta">
            <span className="pod-cover-badge">{podcasts?.length || 0} episodios</span>
          </div>
        </div>
      </div>

      {/* Episode List */}
      {podcasts && podcasts.length > 0 && (
        <div className="pod-list">
          <div className="pod-list-header">
            <span>Episodios</span>
          </div>

          {podcasts.map((podcast, index) => {
            const epNumber = podcasts.length - index;
            const playing = isPlaying(podcast.podCastClubID);
            const progress = getProgress(podcast.podCastClubID);
            const isActive = playingId === podcast.podCastClubID;

            return (
              <div
                className={`pod-episode ${isActive ? 'pod-episode--active' : ''}`}
                key={podcast.podCastClubID}
              >
                <button
                  className={`pod-play-btn ${playing ? 'pod-play-btn--playing' : ''}`}
                  onClick={() => handlePlay(podcast)}
                >
                  {playing ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <rect x="6" y="4" width="4" height="16" rx="1" />
                      <rect x="14" y="4" width="4" height="16" rx="1" />
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <polygon points="5 3 19 12 5 21 5 3" />
                    </svg>
                  )}
                </button>

                <div className="pod-episode-info">
                  <div className="pod-episode-number">EP. {epNumber}</div>
                  <h3 className="pod-episode-title">{podcast.titulo}</h3>
                  {podcast.descripcion && (
                    <p className="pod-episode-desc">{podcast.descripcion}</p>
                  )}

                  {isActive && duration > 0 && (
                    <div className="pod-progress-wrap">
                      <div
                        className="pod-progress-bar"
                        onClick={(e) => handleSeek(e, podcast.podCastClubID)}
                      >
                        <div
                          className="pod-progress-fill"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <div className="pod-progress-time">
                        <span>{formatTime(currentTime)}</span>
                        <span>{formatTime(duration)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default Podcasts;
